// Screen recording via the standard browser APIs:
//   - getDisplayMedia → MediaStream of the user-picked screen/window/tab
//   - getUserMedia    → microphone audio (so narration is captured)
//   - MediaRecorder   → encodes the combined stream into a webm/mp4 blob
//
// We can't request screen capture without a user gesture, so the caller
// triggers `startRecording()` from inside a button onClick handler.
//
// AUDIO: `getDisplayMedia({ audio: true })` only ever yields *system/tab*
// audio, and only when the user opts in via the share picker (and not at all
// for window/full-screen shares on some platforms). It NEVER includes the
// microphone. For bug-report videos the useful audio is almost always the
// reporter narrating, i.e. the mic. So we capture the mic separately via
// getUserMedia and mix it with any system audio into a single track — because
// MediaRecorder only encodes the first audio track of its input stream.

export type AudioStatus = {
  /** True when a live microphone track was captured. */
  mic: boolean;
  /** True when the share picker yielded a system/tab audio track. */
  system: boolean;
};

export type RecordingSession = {
  start(): Promise<void>;
  stop(): Promise<Blob>;
  cancel(): void;
  isRecording(): boolean;
  /** Subscribe to stream-end events fired by the browser (e.g. user clicks
   * the "Stop sharing" banner). The callback is invoked at most once. */
  onStreamEnded(cb: () => void): void;
  /** What audio sources ended up in the recording. Valid after `start()`. */
  getAudioStatus(): AudioStatus;
};

/**
 * Negotiate a MediaRecorder MIME type that the current browser supports.
 * Chrome/Edge prefer webm/vp9, Safari prefers mp4/h264, Firefox webm/vp8.
 * Codecs that bundle an audio codec (opus) are listed first so audio is
 * actually encoded when present.
 */
function pickMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return "";
}

export function createRecordingSession(): RecordingSession {
  // Source streams + derived recording stream are all tracked so teardown can
  // stop every track and release the mic/AudioContext.
  let displayStream: MediaStream | null = null;
  let micStream: MediaStream | null = null;
  let recordStream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let endedCallback: (() => void) | null = null;
  let endedFired = false;
  let mimeType = "";
  let audioStatus: AudioStatus = { mic: false, system: false };

  function fireEnded() {
    if (endedFired) return;
    endedFired = true;
    endedCallback?.();
  }

  // Stop every track from every stream and release the AudioContext. Stopping
  // a track twice is a no-op, so overlap between streams (the display video
  // track lives in both displayStream and recordStream) is harmless.
  function teardown() {
    recordStream?.getTracks().forEach((t) => t.stop());
    displayStream?.getTracks().forEach((t) => t.stop());
    micStream?.getTracks().forEach((t) => t.stop());
    if (audioContext && audioContext.state !== "closed") {
      void audioContext.close();
    }
    recordStream = null;
    displayStream = null;
    micStream = null;
    audioContext = null;
    recorder = null;
  }

  return {
    async start() {
      if (recorder) throw new Error("Recording already started");
      if (typeof navigator.mediaDevices?.getDisplayMedia !== "function") {
        throw new Error(
          "Your browser doesn't support screen recording. Try Chrome, Edge, or Firefox.",
        );
      }

      // 1. Screen/window/tab. Must be first while transient activation from
      //    the click is still valid. `audio: true` opportunistically grabs
      //    system/tab audio if the user shares it.
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });

      // 2. Microphone — captured separately so narration is recorded. A denial
      //    or absent device must NOT abort the recording: silent screen-only
      //    captures are still useful, so we swallow the error and continue.
      try {
        if (typeof navigator.mediaDevices?.getUserMedia === "function") {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
        }
      } catch {
        micStream = null;
      }

      const videoTracks = displayStream.getVideoTracks();
      const systemAudio = displayStream.getAudioTracks();
      const micAudio = micStream?.getAudioTracks() ?? [];
      audioStatus = { mic: micAudio.length > 0, system: systemAudio.length > 0 };

      // 3. Assemble the stream MediaRecorder will encode.
      if (systemAudio.length + micAudio.length <= 1) {
        // Zero or one audio source — MediaRecorder handles a single audio
        // track natively, no mixing needed.
        recordStream = new MediaStream([...videoTracks, ...systemAudio, ...micAudio]);
      } else {
        // Two sources (system + mic). MediaRecorder only encodes the FIRST
        // audio track, so we mix both into one track via Web Audio. The
        // destination is a MediaStreamDestination (not the speakers), so
        // there's no playback / feedback loop.
        const Ctx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctx) {
          audioContext = new Ctx();
          // Created after an await, so it may start suspended — resume it.
          void audioContext.resume();
          const dest = audioContext.createMediaStreamDestination();
          if (systemAudio.length) {
            audioContext.createMediaStreamSource(new MediaStream(systemAudio)).connect(dest);
          }
          if (micAudio.length) {
            audioContext.createMediaStreamSource(new MediaStream(micAudio)).connect(dest);
          }
          recordStream = new MediaStream([...videoTracks, ...dest.stream.getAudioTracks()]);
        } else {
          // No Web Audio support — fall back to the mic alone (the more useful
          // of the two for a bug report).
          recordStream = new MediaStream([...videoTracks, ...micAudio]);
        }
      }

      // Browser-level "Stop sharing" — fires onended on the display video track.
      videoTracks[0]?.addEventListener("ended", fireEnded);

      mimeType = pickMimeType();
      const options: MediaRecorderOptions = mimeType
        ? { mimeType, videoBitsPerSecond: 2_500_000 }
        : { videoBitsPerSecond: 2_500_000 };

      recorder = new MediaRecorder(recordStream, options);
      chunks = [];
      recorder.addEventListener("dataavailable", (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      });
      recorder.start(1_000); // 1s timeslice so we don't lose everything on a crash.
    },

    async stop(): Promise<Blob> {
      if (!recorder) throw new Error("Recording not started");
      const r = recorder;
      const blob = await new Promise<Blob>((resolve, reject) => {
        r.addEventListener("stop", () => {
          const type = mimeType.split(";")[0] || "video/webm";
          resolve(new Blob(chunks, { type }));
        });
        r.addEventListener("error", (e) => {
          reject((e as ErrorEvent).error ?? new Error("MediaRecorder error"));
        });
        try {
          r.stop();
        } catch (err) {
          reject(err);
        }
      });
      // Stop all tracks so the browser drops the "sharing" indicator and the
      // mic light turns off.
      teardown();
      return blob;
    },

    cancel() {
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // ignored
        }
      }
      teardown();
      chunks = [];
    },

    isRecording() {
      return recorder?.state === "recording";
    },

    onStreamEnded(cb: () => void) {
      endedCallback = cb;
    },

    getAudioStatus() {
      return audioStatus;
    },
  };
}

export function detectVideoExtension(blob: Blob): "webm" | "mp4" {
  return blob.type.startsWith("video/mp4") ? "mp4" : "webm";
}

export function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}
